/**
 * Migrate anonymous local rows to the signed-in Cognito user.
 *
 * Triggered the first time useSyncEngine detects a userId change from
 * anon-${uuid} → Cognito sub. Re-stamps every local row with the new userId
 * and enqueues create entries so the push loop uploads them.
 *
 * The anon UUID is kept in meta for audit / re-migration guard.
 *
 * Safety:
 *   - Before enqueueing, we fetch the user's existing remote sessions and
 *     skip any local row whose id is already present on the server. This
 *     avoids duplicating sessions when the user previously recorded on
 *     another device, signed into the same account, then later returned
 *     to this device. Skipped rows are marked SYNCED locally.
 *   - GPS tracks (separate `db.tracks` row keyed by sessionId) travel
 *     with the create payload — the original implementation dropped them.
 *   - If the dedup fetch fails (offline, transient 5xx), we fall back to
 *     enqueueing everything rather than blocking the migration entirely.
 */

import * as sessionsRepo from '../db/sessions';
import * as queue from '../db/queue';
import { db, SYNC_STATUS } from '../db';
import { setMeta } from '../db/meta';
import { getSessions } from '../api';

export async function migrateAnonymousToUser({ fromUserId, toUserId }) {
  if (!fromUserId || !toUserId) return { rekeyed: 0, enqueued: 0, skipped: 0 };
  if (!fromUserId.startsWith('anon-')) return { rekeyed: 0, enqueued: 0, skipped: 0 };

  // 1. Re-stamp rows + their tracks
  const rekeyed = await sessionsRepo.rekeyUser(fromUserId, toUserId);

  // 2. Build a set of session ids the server already knows about. Best
  //    effort — if the API call fails (offline / 5xx) we still proceed
  //    with the migration; duplicate detection is server-side belt-and-
  //    braces, the client-side check is just to avoid wasted bandwidth.
  const remoteIds = new Set();
  try {
    const response = await getSessions({ limit: 500 });
    const list = response?.sessions || response?.data?.sessions || response?.data || response || [];
    for (const r of Array.isArray(list) ? list : []) {
      // Server rows use `sessionId`; local rows use `id`. Handle both.
      const id = r?.sessionId || r?.id;
      if (id) remoteIds.add(id);
    }
  } catch (err) {
    console.warn('[migrate] remote session list fetch failed, proceeding without dedup:', err?.message || err);
  }

  // 3. Enqueue creates for every local row that isn't already on the server
  const rows = await db.sessions.where('userId').equals(toUserId).toArray();
  let enqueued = 0;
  let skipped = 0;
  for (const row of rows) {
    if (row.deletedAt) continue;
    if (row.syncStatus === SYNC_STATUS.SYNCED) continue;
    if (remoteIds.has(row.id)) {
      await sessionsRepo.markSynced(row.id);
      skipped += 1;
      continue;
    }
    // Strip Dexie-only metadata before sending to the API
    const { syncStatus, lastUpdatedAt, lastSyncError, deletedAt, userId: _u, ...sessionPayload } = row;
    // Include the GPS track so the upload doesn't silently drop the
    // user's points — the track lives in a separate Dexie table.
    const track = await sessionsRepo.getTrack(row.id);
    await queue.enqueue({
      entity: 'session',
      op: 'create',
      recordId: row.id,
      payload: track ? { ...sessionPayload, track } : sessionPayload,
    });
    enqueued += 1;
  }

  await setMeta('migratedFromAnon', { fromUserId, toUserId, at: new Date().toISOString() });

  return { rekeyed, enqueued, skipped };
}
